import { jsx } from 'react/jsx-runtime';
import { Button } from './Button.js';
import { Flex } from './Flex.js';
import { P } from './Typography.js';
import { renderCssBasedOnColorScheme } from '../theme/renderCssBasedOnColorScheme.js';
import { cssVar } from '../theme/variables.js';
import { motion } from 'framer-motion';
import React__default, { forwardRef, useMemo, useRef, useEffect } from 'react';
import { mergeRefs } from 'react-merge-refs';
import { styled, css } from 'styled-components';
import { useDefaultedEvent } from '@fabric-app/hooks/useDefaultedEvent';
import { MarkdownRender } from '../markdown/components/MarkdownRender.js';
import { ScrollArea } from './ScrollArea.js';
import { mediaHover } from '../constants/styled-utils.js';
import { useTextAreaAutoResize } from '../utils/useTextAreaAutoResize.js';

const ConversationItem = styled(Flex).attrs((p) => {
  return {
    gap: "element",
    direction: "column",
    ...p
  };
}).withConfig({
  displayName: "Conversation__ConversationItem",
  componentId: "sc-64e2beaa-0"
})([
  `width:100%;padding:0.625rem;`
]);
const ConversationActionWrap = styled(Flex).attrs((p) => {
  return {
    gap: "text",
    ...p
  };
}).withConfig({
  displayName: "Conversation__ConversationActionWrap",
  componentId: "sc-64e2beaa-1"
})([
  `width:100%;`,
  `:nth-child(2){padding:0.625rem;background:`,
  `;border-radius:`,
  `;}`
], ConversationItem, cssVar["color-bg-senary"], cssVar["radius-input"]);
const ConversationActionIcons = styled(Flex).attrs((p) => {
  return {
    gap: "text",
    direction: "column",
    ...p
  };
}).withConfig({
  displayName: "Conversation__ConversationActionIcons",
  componentId: "sc-64e2beaa-2"
})([
  `padding:12px 0;`
]);
const ConversationItemHeader = styled(Flex).attrs((p) => {
  return {
    gap: "element",
    alignItems: "center",
    ...p
  };
}).withConfig({
  displayName: "Conversation__ConversationItemHeader",
  componentId: "sc-64e2beaa-3"
})([
  `user-select:none;`
]);
const ConversationItemHeaderName = styled(P).attrs((p) => {
  return {
    weight: 600,
    size: "sm-plus",
    ...p
  };
}).withConfig({
  displayName: "Conversation__ConversationItemHeaderName",
  componentId: "sc-64e2beaa-4"
})([
  `flex-grow:1;`
]);
const ConversationItemBody = styled(MarkdownRender).withConfig({
  displayName: "Conversation__ConversationItemBody",
  componentId: "sc-64e2beaa-5"
})([
  `font-size:0.8125rem;line-height:1.15rem;color:`,
  `;word-break:break-word;hyphens:auto;user-select:text !important;-webkit-user-select:text !important;*{user-select:text !important;-webkit-user-select:text !important;}--margin-block-start:0.5em;--margin-block-end:0.5em;--padding-inline-start:1em;--heading-scale:1.2;`
], cssVar["color-text-primary"]);
const ConversationList = styled(Flex).attrs((p) => {
  return {
    gap: 10,
    direction: "column",
    alignItems: "center",
    flexGrow: 1,
    minHeight: "100%",
    ...p
  };
}).withConfig({
  displayName: "Conversation__ConversationList",
  componentId: "sc-64e2beaa-6"
})([
  `padding:0.75rem;max-width:800px;margin:0 auto;user-select:auto;-webkit-user-select:auto;[data-assistant-fullscreen='true'] &{padding:0.75rem;}*{user-select:auto;-webkit-user-select:auto;}`
]);
const ConversationInfoTag = styled.div.withConfig({
  displayName: "Conversation__ConversationInfoTag",
  componentId: "sc-64e2beaa-7"
})([
  `display:inline-flex;align-items:center;gap:0.25rem;align-self:`,
  `;vertical-align:middle;text-align:center;min-width:2.5rem;height:1.3125rem;background-color:`,
  `;outline:1px solid `,
  `;color:`,
  `;border-radius:0.5rem;padding:0 0.375rem;font-size:0.8125rem;font-weight:500;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;max-width:100%;transition:color 0.2s ease-in-out;`,
  ``
], ({ alignSelf = "center" }) => alignSelf, cssVar["color-bg-contrast"], cssVar["color-border-primary"], cssVar["color-text-tertiary"], ({ hoverable }) => hoverable && css([
  `cursor:pointer;`,
  `{&:hover{color:`,
  `;}&[data-hovering='true']{color:`,
  `;}}`
], mediaHover, cssVar["color-text-primary"], cssVar["color-text-primary"]));
const ConversationInfoTagText = styled.span.withConfig({
  displayName: "Conversation__ConversationInfoTagText",
  componentId: "sc-64e2beaa-8"
})([
  `overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%;&[data-quote]{flex-shrink:0;}&[data-quote] + &:not([data-quote]){margin-left:-0.25rem;margin-right:-0.25rem;}`
]);
const ConversationForm = styled(motion.form).withConfig({
  displayName: "Conversation__ConversationForm",
  componentId: "sc-64e2beaa-9"
})([
  `position:relative;display:flex;flex-direction:column;justify-content:center;align-items:center;gap:0.625rem;flex-shrink:0;padding:0.75rem;border-top:2px solid `,
  `;[data-fullscreen='true'] &{padding:1rem;}[data-assistant-fullscreen='true'] &{border-radius:12px 12px 0px 0px;border-left:2px solid `,
  `;border-right:2px solid `,
  `;}`
], cssVar["color-border-secondary"], cssVar["color-border-secondary"], cssVar["color-border-secondary"]);
const ConversationFormField = styled(Flex).attrs((p) => {
  return {
    gap: "element",
    alignItems: "center",
    as: "label",
    ...p
  };
}).withConfig({
  displayName: "Conversation__ConversationFormField",
  componentId: "sc-64e2beaa-10"
})([
  `width:100%;padding:9px 0.5rem 9px 1rem;background:`,
  `;overflow:hidden;border-radius:0.625rem;border:1px solid transparent;&:focus-within{`,
  `}`
], cssVar["color-border-tertiary"], renderCssBasedOnColorScheme({
  light: css([
    `outline:1px solid #30f;box-shadow:0px 4px 24px 0px rgba(51,0,255,0.2);`
  ]),
  dark: css([
    `outline:1px solid white;box-shadow:0px 4px 24px 0px rgba(255,255,255,0.2);`
  ])
}));
const StyledConversationFormTextAreaWrapper = styled.div.withConfig({
  displayName: "Conversation__StyledConversationFormTextAreaWrapper",
  componentId: "sc-64e2beaa-11"
})([
  `display:flex;align-items:center;max-height:68px;min-height:17px;flex-grow:1;`
]);
const StyledConversationFormTextArea = styled.textarea.withConfig({
  displayName: "Conversation__StyledConversationFormTextArea",
  componentId: "sc-64e2beaa-12"
})([
  `flex-grow:1;font-size:0.875rem;line-height:1.0625rem;font-weight:500;width:100%;color:`,
  `;background:none;border:none;outline:none;resize:none;display:flex;overflow:visible;word-break:break-word;white-space:pre-wrap;&::placeholder{color:`,
  `;}`
], cssVar["color-text-primary"], cssVar["color-text-placeholder"]);
const ConversationFormTextArea = /* @__PURE__ */ forwardRef((props, propRef) => {
  const [innerRef, setInnerRef] = React__default.useState(null);
  const [content, setContent] = React__default.useState("");
  const ref = useMemo(() => mergeRefs([
    propRef,
    setInnerRef
  ]), [
    propRef
  ]);
  const height = useTextAreaAutoResize(innerRef, content, {
    minHeight: 17,
    maxHeight: 68
  });
  return /* @__PURE__ */ jsx(StyledConversationFormTextAreaWrapper, {
    style: {
      height
    },
    children: /* @__PURE__ */ jsx(ScrollArea.Default, {
      style: {
        height,
        width: "100%"
      },
      children: /* @__PURE__ */ jsx(StyledConversationFormTextArea, {
        ref,
        rows: 1,
        ...props,
        onChange: useDefaultedEvent((e) => {
          setContent(e.target.value);
        }, props.onChange)
      })
    })
  });
});
const ConversationFormButton = styled(Button).attrs((p) => {
  return {
    variant: "primary",
    shape: "round",
    size: "xss",
    ...p
  };
}).withConfig({
  displayName: "Conversation__ConversationFormButton",
  componentId: "sc-64e2beaa-13"
})([
  `&:disabled{background:#d4d4d4;}`
]);
ConversationFormTextArea.displayName = "ConversationFormTextArea";
const ConversationFormControl = styled(Button).attrs((p) => {
  return {
    size: "xs",
    variant: "assistant-combobox",
    ...p
  };
}).withConfig({
  displayName: "Conversation__ConversationFormControl",
  componentId: "sc-64e2beaa-14"
})([
  `font-size:13px;`
]);
const ConversationFormControlGroup = styled(Flex).attrs((p) => {
  return {
    gap: 6,
    minWidth: "100%",
    ...p
  };
}).withConfig({
  displayName: "Conversation__ConversationFormControlGroup",
  componentId: "sc-64e2beaa-15"
})([
  ``
]);
const ConversationInputArea = styled(Flex).attrs((p) => {
  return {
    direction: "column",
    gap: 12,
    ...p
  };
}).withConfig({
  displayName: "Conversation__ConversationInputArea",
  componentId: "sc-64e2beaa-16"
})([
  `position:relative;width:100%;max-width:808px;margin:0 auto;`
]);
const ConversationQuickActions = styled(Flex).attrs((p) => {
  return {
    gap: 12,
    direction: "column",
    "data-quick-actions": true,
    ...p
  };
}).withConfig({
  displayName: "Conversation__ConversationQuickActions",
  componentId: "sc-64e2beaa-17"
})([
  `flex-shrink:0;`
]);
const ConversationQuickActionsList = ({ children }) => {
  const viewportRef = useRef(null);
  useEffect(() => {
    const viewport = viewportRef.current;
    const parent = viewport?.closest('[data-quick-actions="true"]');
    if (!parent || !viewport) return;
    let scrollAmount = 0;
    let isScrolling = false;
    let animationFrameId;
    const smoothScroll = () => {
      viewport.scrollLeft += scrollAmount * 0.1;
      scrollAmount *= 0.9;
      if (Math.abs(scrollAmount) > 0.5) {
        animationFrameId = requestAnimationFrame(smoothScroll);
      } else {
        isScrolling = false;
        scrollAmount = 0;
      }
    };
    const onWheel = (e) => {
      if (e.deltaY === 0) return;
      e.preventDefault();
      scrollAmount += e.deltaY;
      if (!isScrolling) {
        isScrolling = true;
        animationFrameId = requestAnimationFrame(smoothScroll);
      }
    };
    parent.addEventListener("wheel", onWheel);
    return () => {
      parent.removeEventListener("wheel", onWheel);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  return /* @__PURE__ */ jsx(ScrollArea.Default, {
    orientation: "horizontal",
    scrollbarVariant: "none",
    viewportRef,
    "data-pan-id-ignore-expanded-resource": "x",
    children: /* @__PURE__ */ jsx(Flex, {
      gap: 12,
      alignItems: "center",
      style: {
        padding: "0 12px"
      },
      children
    })
  });
};
const ConversationQuickAction = styled.button.withConfig({
  displayName: "Conversation__ConversationQuickAction",
  componentId: "sc-64e2beaa-18"
})([
  `display:flex;align-items:center;justify-content:center;padding:9px 12px;height:2.125rem;border-radius:`,
  `;white-space:nowrap;transition:box-shadow 0.2s ease-in-out,border-color 0.2s ease-in-out;border:1px solid transparent;`,
  ` `,
  `{&:hover:enabled{box-shadow:0 0 10rem 0 rgba(`,
  `,0.1) inset;}}&:focus-visible{outline:none;border-color:`,
  `;}`
], cssVar["radius-input-lg"], renderCssBasedOnColorScheme({
  light: css([
    `background:`,
    `;border-color:rgba(227,228,232,0.8);color:`,
    `;`
  ], cssVar["color-bg-primary"], cssVar["color-text-tertiary"]),
  dark: css([
    `background:rgba(102,102,102,0.5);color:`,
    `;border-color:transparent;`
  ], cssVar["color-text-primary"])
}), mediaHover, cssVar["color-bg-primary-reverse-rgb"], cssVar["color-app-primary-text"]);
const ConversationQuickActionsContext = styled(P).attrs((p) => {
  return {
    size: "xs",
    weight: 500,
    style: {
      ...p.style,
      userSelect: "none"
    },
    ...p
  };
}).withConfig({
  displayName: "Conversation__ConversationQuickActionsContext",
  componentId: "sc-64e2beaa-19"
})([
  ``,
  ` display:flex;align-items:center;justify-content:space-between;`
], renderCssBasedOnColorScheme({
  light: css([
    `color:`,
    `;`
  ], cssVar["color-text-tertiary"]),
  dark: css([
    `color:`,
    `;`
  ], cssVar["color-text-primary"])
}));
const Conversation = {
  ActionWrap: ConversationActionWrap,
  ActionIcons: ConversationActionIcons,
  Item: ConversationItem,
  ItemHeader: ConversationItemHeader,
  ItemHeaderName: ConversationItemHeaderName,
  ItemBody: ConversationItemBody,
  List: ConversationList,
  InfoTag: ConversationInfoTag,
  InfoTagText: ConversationInfoTagText,
  InputArea: ConversationInputArea,
  Form: ConversationForm,
  FormField: ConversationFormField,
  FormTextArea: ConversationFormTextArea,
  FormButton: ConversationFormButton,
  FormControl: ConversationFormControl,
  FormControlGroup: ConversationFormControlGroup,
  QuickActions: ConversationQuickActions,
  QuickAction: ConversationQuickAction,
  QuickActionsList: ConversationQuickActionsList,
  QuickActionsContext: ConversationQuickActionsContext
};

export { Conversation, ConversationActionIcons, ConversationActionWrap, ConversationForm, ConversationFormButton, ConversationFormControl, ConversationFormControlGroup, ConversationFormField, ConversationFormTextArea, ConversationInfoTag, ConversationInfoTagText, ConversationInputArea, ConversationItem, ConversationItemBody, ConversationItemHeader, ConversationItemHeaderName, ConversationList, ConversationQuickAction, ConversationQuickActions, ConversationQuickActionsContext, ConversationQuickActionsList };
//# sourceMappingURL=Conversation.js.map
